import { Td } from "../../SetiTable.styled";
import coachingVideoMapping from "../../../constants/CoachingVideoMapping";

import {
  CRITICAL_COLOR,
  CountTd,
  FilteredRangeTd,
  PPD_COLOR,
  RangeTd,
  RangePlayerTd,
  RangeBiomechanicsTd,
  RangePoseTd,
  RangeVideoTd,
  WARN_COLOR,
} from "../ReportElements";

const columnsPerGroup = {
  showPitches: 3,
  showVideo: 10,
  showReviewAngles: 2,
  showHitting: 2,
  showPlayers: 4,
};

export function DataColumns({
  game,
  tracking,
  isPreHawkeye,
  groupsToShow,
  showPitchesAsCount = false,
  showBipAsCount = false,
  showSwingsAsCount = false,
}) {
  const { status = {}, coachingVideo = [], trackingVersion = {} } = game;

  let trackingVersionId = trackingVersion.id;

  if (status.codedGameState === "D") {
    let colSpan = 0;
    const effectiveGroups = {
      ...groupsToShow,
      showReviewAngles: groupsToShow.showVideo && groupsToShow.showReviewAngles,
    };
    Object.entries(effectiveGroups).map(([key, show]) => {
      colSpan += show ? columnsPerGroup[key] ?? 0 : 0;
    });
    return (
      <Td bgcolor={PPD_COLOR} colSpan={colSpan}>
        PPD
      </Td>
    );
  }

  const {
    pitches,
    pitchesOpticallyTracked,
    broadcastVideo,
    alternateBroadcastVideo,
    chHighSideviewVideo,
    highHomeVideo,
    midHomeVideo,
    broadcastReview,
    alternateBroadcastReview,
    centerfieldVideo,
    pitchcastVideo,
    firstBasePitcherSideViewVideo,
    thirdBasePitcherSideViewVideo,
    firstBaseBatterSideViewVideo,
    thirdBaseBatterSideViewVideo,
    pitchesWithSpin,
    reducedSpinConfidence,
    bip,
    totalPlays,
    totalHits,
    swingsTracked = 0,
    totalSwings = 0,
    batterBiomechanicsAvailable = 0,
    pitcherBiomechanicsAvailable = 0,
    playerTrackingCompleteness = 0,
    poseTrackingCompleteness = 0,
    challenges = 0,
  } = tracking;

  const bipWarnThreshold = isPreHawkeye ? 0.8 : 0.95;
  const bipAlertThreshold = isPreHawkeye ? 0.9 : 0.99999;

  return (
    <>
      {groupsToShow.showPitches && (
        <>
          {showPitchesAsCount ? (
            <CountTd count={pitches} totalPlays={totalPlays} />
          ) : (
            <RangeTd
              count={pitches}
              totalPitches={totalPlays}
              stops={[[0, CRITICAL_COLOR], [0.99, WARN_COLOR], [0.995]]}
            />
          )}
          <FilteredRangeTd
            filtered={!isPreHawkeye} //Column is only shown pre HawkEye
            count={pitchesOpticallyTracked}
            totalPitches={pitches}
            stops={[[0, CRITICAL_COLOR], [0.97, WARN_COLOR], [0.98]]}
          />
          <RangeTd
            count={pitchesWithSpin}
            totalPitches={pitches}
            trackingVersion={trackingVersionId}
            stops={[[0, CRITICAL_COLOR], [0.9, WARN_COLOR], [0.95]]}
          />
          <RangeTd
            count={reducedSpinConfidence}
            totalPitches={pitches}
            trackingVersion={trackingVersionId}
            stops={[[0], [0.001, WARN_COLOR], [0.01, CRITICAL_COLOR]]}
            ceil={true}
          />
        </>
      )}
      {groupsToShow.showVideo && (
        <>
          <RangeVideoTd
            count={broadcastVideo}
            totalPitches={totalPlays}
            angle={coachingVideoMapping.broadcast}
            availableAngles={coachingVideo}
            stops={[[0, CRITICAL_COLOR], [0.995, WARN_COLOR], [0.99999]]}
          />
          <RangeVideoTd
            count={alternateBroadcastVideo}
            totalPitches={totalPlays}
            angle={coachingVideoMapping.alternateBroadcast}
            availableAngles={coachingVideo}
            stops={[[0, CRITICAL_COLOR], [0.995, WARN_COLOR], [0.99999]]}
          />
          <RangeVideoTd
            count={pitchcastVideo}
            totalPitches={totalPlays}
            angle={coachingVideoMapping.pitchcast}
            availableAngles={coachingVideo}
            stops={[[0, CRITICAL_COLOR], [0.995, WARN_COLOR], [0.99999]]}
          />
          <RangeVideoTd
            count={centerfieldVideo}
            totalPitches={totalPlays}
            angle={coachingVideoMapping.centerfield}
            availableAngles={coachingVideo}
            stops={[[0, CRITICAL_COLOR], [0.995, WARN_COLOR], [0.99999]]}
          />
          <RangeVideoTd
            count={isPreHawkeye ? chHighSideviewVideo : highHomeVideo}
            totalPitches={totalPlays}
            angle={
              isPreHawkeye
                ? coachingVideoMapping.chHighSideview
                : coachingVideoMapping.highHome
            }
            availableAngles={coachingVideo}
            stops={[[0, CRITICAL_COLOR], [0.995, WARN_COLOR], [0.99999]]}
          />
          <RangeVideoTd
            count={firstBaseBatterSideViewVideo}
            totalPitches={totalPlays}
            angle={coachingVideoMapping.firstBaseBatterSideview}
            availableAngles={coachingVideo}
            stops={[[0, CRITICAL_COLOR], [0.995, WARN_COLOR], [0.99999]]}
          />
          <RangeVideoTd
            count={thirdBaseBatterSideViewVideo}
            totalPitches={totalPlays}
            angle={coachingVideoMapping.thirdBaseBatterSideview}
            availableAngles={coachingVideo}
            stops={[[0, CRITICAL_COLOR], [0.995, WARN_COLOR], [0.99999]]}
          />
          <RangeVideoTd
            count={firstBasePitcherSideViewVideo}
            totalPitches={totalPlays}
            angle={coachingVideoMapping.firstBasePitcherSideview}
            availableAngles={coachingVideo}
            stops={[[0, CRITICAL_COLOR], [0.995, WARN_COLOR], [0.99999]]}
          />
          <RangeVideoTd
            count={thirdBasePitcherSideViewVideo}
            totalPitches={totalPlays}
            angle={coachingVideoMapping.thirdBasePitcherSideview}
            availableAngles={coachingVideo}
            stops={[[0, CRITICAL_COLOR], [0.995, WARN_COLOR], [0.99999]]}
          />
          <RangeVideoTd
            count={midHomeVideo}
            totalPitches={totalPlays}
            angle={coachingVideoMapping.midHome}
            availableAngles={coachingVideo}
            stops={[[0, CRITICAL_COLOR], [0.995, WARN_COLOR], [0.99999]]}
          />
          {groupsToShow.showReviewAngles && (
            <>
              <RangeVideoTd
                count={broadcastReview}
                totalPitches={challenges}
                angle={coachingVideoMapping.broadcastReview}
                availableAngles={coachingVideo}
                stops={[[0, CRITICAL_COLOR], [0.995, WARN_COLOR], [0.99999]]}
              />
              <RangeVideoTd
                count={alternateBroadcastReview}
                totalPitches={challenges}
                angle={coachingVideoMapping.alternateBroadcastReview}
                availableAngles={coachingVideo}
                stops={[[0, CRITICAL_COLOR], [0.995, WARN_COLOR], [0.99999]]}
              />
            </>
          )}
        </>
      )}
      {groupsToShow.showHitting && (
        <>
          {showBipAsCount ? (
            <CountTd count={bip} totalPlays={totalHits} />
          ) : (
            <RangeTd
              count={bip}
              totalPitches={totalHits}
              stops={[
                [0, CRITICAL_COLOR],
                [bipWarnThreshold, WARN_COLOR],
                [bipAlertThreshold],
              ]}
            />
          )}
          {showSwingsAsCount ? (
            <CountTd count={swingsTracked} totalPlays={totalSwings} />
          ) : (
            <RangeBiomechanicsTd
              count={swingsTracked}
              totalPitches={totalSwings}
              trackingVersion={trackingVersionId}
              stops={[[0, CRITICAL_COLOR], [0.9, WARN_COLOR], [0.95]]}
            />
          )}
        </>
      )}
      {groupsToShow.showPlayers && (
        <>
          <RangeBiomechanicsTd
            count={batterBiomechanicsAvailable}
            totalPitches={totalPlays}
            trackingVersion={trackingVersionId}
            stops={[[0, CRITICAL_COLOR], [0.9, WARN_COLOR], [0.95]]}
          />
          <RangeBiomechanicsTd
            count={pitcherBiomechanicsAvailable}
            totalPitches={totalPlays}
            trackingVersion={trackingVersionId}
            stops={[[0, CRITICAL_COLOR], [0.9, WARN_COLOR], [0.95]]}
          />
          <RangePlayerTd
            count={playerTrackingCompleteness}
            totalPitches={totalPlays}
            trackingVersion={trackingVersionId}
            stops={[[0, CRITICAL_COLOR], [0.9, WARN_COLOR], [0.95]]}
          />
          <RangePoseTd
            count={poseTrackingCompleteness}
            totalPitches={totalPlays}
            trackingVersion={trackingVersionId}
            stops={[[0, CRITICAL_COLOR], [0.9, WARN_COLOR], [0.95]]}
          />
        </>
      )}
    </>
  );
}
